import { memo, useEffect, useRef, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

function formatAmount(n) {
  if (!n && n !== 0) return '';
  const num = Number(n);
  if (num >= 100000) return `${num / 100000}L`;
  if (num >= 1000) return `${num / 1000}K`;
  return String(num);
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0' || item.gstatus === 0;
}

function bookCls(v) {
  if (!v) return 'book-black';
  return v < 0 ? 'book-red' : 'book-green';
}

// Bootstrap collapse animation (.collapsing CSS app.css me)
function CollapseBody({ id, open, children }) {
  const ref = useRef(null);
  const first = useRef(true);
  const [cls, setCls] = useState('collapse show');
  const [style, setStyle] = useState(undefined);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const el = ref.current;
    if (!el) return;
    let raf1;
    let raf2;
    if (open) {
      setCls('collapsing');
      setStyle({ height: '0px' });
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setStyle({ height: `${el.scrollHeight}px` }));
      });
    } else {
      setStyle({ height: `${el.scrollHeight}px` });
      raf1 = requestAnimationFrame(() => {
        setCls('collapsing');
        raf2 = requestAnimationFrame(() => setStyle({ height: '0px' }));
      });
    }
    const t = setTimeout(() => {
      setCls(open ? 'collapse show' : 'collapse');
      setStyle(undefined);
    }, 350);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t);
    };
  }, [open]);

  return (
    <div id={id} ref={ref} className={`bet-table-body ${cls} container-fluid container-fluid-5`} style={style}>
      {children}
    </div>
  );
}

// Goal market — reference: market-6, header pe hi collapse toggle (bina
// arrow img ke), body grid col-6 col-md-12, back-only bl-box (data-title
// box pe), book hamesha visible (book-black at 0), fancy-min-max me
// Min/Max newline-separated.
const GoalMarket = function GoalMarket({ title, marketId, items, onBet, exposures }) {
  const [open, setOpen] = useState(true);
  if (!items || items.length === 0) return null;

  return (
    <div className="market-6">
      <div className="bet-table">
        <div
          data-toggle="collapse"
          data-target={`#${marketId}`}
          aria-expanded={open ? 'true' : 'false'}
          className={`bet-table-header${open ? '' : ' collapsed'}`}
          onClick={() => setOpen((v) => !v)}
        >
          <div className="nation-name">
            <span title={title}>
              {'\n          ' + title + '\n        '}
            </span>
          </div>
        </div>
        {' '}
        <CollapseBody id={marketId} open={open}>
          <div className="row row5 d-none-mobile">
            <div className="col-6 col-md-12">
              <div className="fancy-tripple">
                <div className="bet-table-row">
                  <div className="nation-name"></div>
                  {' '}
                  <div className="back bl-title back-title">Back</div>
                </div>
              </div>
            </div>
          </div>
          {' '}
          <div className="row row5">
            {items.map((item, i) => {
              const suspended = isSusp(item);
              const b1 = item?.b1 || item?.b || item?.rate || '';
              const bs1 = item?.bs1 || item?.bs || '';
              const book = roundBook(exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat] ?? 0);
              return (
                <div key={item?.sid ?? i} className="col-6 col-md-12">
                  <div className="fancy-tripple">
                    <div className="bet-table-mobile-row d-none-desktop">
                      <div className="bet-table-mobile-team-name">
                        <span>{item?.nat ?? ''}</span>
                        {' '}
                        <span className={bookCls(book)}>{fmtBook(book)}</span>
                      </div>
                    </div>
                    {' '}
                    <div className="bet-table-row">
                      <div className="nation-name d-none-mobile">
                        <p>
                          {'\n                    ' + (item?.nat ?? '') + '\n                  '}
                        </p>
                        {' '}
                        <p className={`mb-0 ${bookCls(book)}`}>{fmtBook(book)}</p>
                      </div>
                      {' '}
                      <div
                        data-title={suspended ? 'SUSPENDED' : 'OPEN'}
                        className={`bl-box back${suspended ? ' no-val suspended' : ''}`}
                        onClick={suspended ? undefined : () => onBet && onBet(item.sid, 'back', b1, item)}
                      >
                        <span className="d-block odds">{suspended ? '—' : b1}</span>
                        {' '}
                        {!suspended && <span className="d-block">{formatAmount(bs1)}</span>}
                      </div>
                      {' '}
                      <div className="fancy-min-max">
                        {'\n                  Min:'}
                        <span>{formatAmount(item?.min ?? '')}</span>
                        {'\n                  Max:'}
                        <span>{formatAmount(item?.max ?? '')}</span>
                      </div>
                    </div>
                    {' '}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapseBody>
      </div>
    </div>
  );
};

// Exclusive method names (not "No Goal" since it appears in both markets)
const EXCLUSIVE_METHODS = ['shot goal', 'header goal', 'penalty goal', 'free kick goal'];

function BetTableGoal({ gameData, onBet, exposures }) {
  const sub = gameData?.sub ?? [];

  let playersMarket;
  let methodsMarket;

  // Try discriminator fields first (API sends subtype: "player" and subtype: "goal")
  const byPlayer = sub.filter((s) => s.subtype === 'player');
  const byMethod = sub.filter((s) => s.subtype === 'goal' || s.subtype === 'method');

  if (byPlayer.length > 0 || byMethod.length > 0) {
    playersMarket = byPlayer;
    methodsMarket = byMethod;
  } else {
    // Split by position: find where the first exclusive method name starts
    const splitIndex = sub.findIndex((s) =>
      EXCLUSIVE_METHODS.includes((s.nat || '').toLowerCase().trim())
    );
    if (splitIndex > 0) {
      playersMarket = sub.slice(0, splitIndex);
      methodsMarket = sub.slice(splitIndex);
    } else {
      playersMarket = sub;
      methodsMarket = [];
    }
  }

  return (
    <>
      <GoalMarket
        title="Who Will Goal Next?"
        marketId="market-player"
        items={playersMarket}
        onBet={onBet}
        exposures={exposures}
      />
      {' '}
      <GoalMarket
        title="Method Of Next Goal"
        marketId="market-goal"
        items={methodsMarket}
        onBet={onBet}
        exposures={exposures}
      />
    </>
  );
}

export default memo(BetTableGoal);
